import {
  AlertOctagon,
  ArrowUp,
  BatteryWarning,
  Box,
  CircleDollarSign,
  FileWarning,
  LockKeyhole,
  MapPin,
  PackageCheck,
  PackageOpen,
  Scale,
  ThermometerSnowflake,
  Truck
} from "lucide-react";
import { Link } from "react-router-dom";
import type { InventoryItem, InventoryUnit } from "../types";
import { Brand } from "./Brand";

export function PublicInventoryRecord({
  unit,
  canEdit
}: {
  unit: InventoryUnit;
  canEdit: boolean;
}) {
  const mainPhoto =
    unit.photos.find((photo) => photo.category === "Container Exterior") || unit.photos[0];
  const otherPhotos = unit.photos.filter((photo) => photo.id !== mainPhoto?.id);
  const warnings = [
    { active: unit.fragile, icon: FileWarning, label: "Fragile", tone: "red" },
    { active: unit.heavy, icon: Scale, label: "Heavy", tone: "amber" },
    { active: unit.keep_upright, icon: ArrowUp, label: "Keep upright", tone: "red" },
    { active: unit.do_not_stack, icon: AlertOctagon, label: "Do not stack", tone: "red" },
    {
      active: unit.temperature_sensitive,
      icon: ThermometerSnowflake,
      label: "Temperature sensitive",
      tone: "blue"
    },
    {
      active: unit.lithium_batteries,
      icon: BatteryWarning,
      label: "Lithium batteries",
      tone: "amber"
    },
    { active: unit.high_value, icon: CircleDollarSign, label: "High value", tone: "amber" }
  ];

  return (
    <div className="public-record-shell">
      <header className="public-record-header">
        <Brand />
        <div>
          <span>Read-only NFC view</span>
          <Link className="button secondary" to={canEdit ? `/inventory/${unit.id}` : "/login"}>
            <LockKeyhole size={17} />
            {canEdit ? "Open editing tools" : "Sign in to update"}
          </Link>
        </div>
      </header>

      <main className="record-page public-record-page">
        <div className="record-heading">
          <div>
            <small>Stock number</small>
            <h1>{unit.stock_number}</h1>
            <span>Inventory name</span>
            <h2>{unit.name}</h2>
          </div>
        </div>

        {mainPhoto ? (
          <img className="record-hero" src={mainPhoto.url} alt={`${unit.name} container`} />
        ) : (
          <div className="record-hero photo-placeholder public-photo-placeholder">
            <Box size={34} />
            <span>No container photo yet</span>
          </div>
        )}

        <div className="record-facts">
          <div>
            <MapPin />
            <span>Current location</span>
            <strong>{unit.current_location?.name || "Not set"}</strong>
          </div>
          <div>
            <Box />
            <span>Destination room</span>
            <strong>{unit.destination_room?.name || "Not set"}</strong>
          </div>
          <div>
            <PackageCheck />
            <span>Packing status</span>
            <strong className="status">{unit.packing_status}</strong>
          </div>
          <div>
            <Truck />
            <span>Move status</span>
            <strong className="status blue">{unit.move_status}</strong>
          </div>
        </div>

        <div className="warning-grid">
          {warnings
            .filter(({ active }) => active)
            .map(({ icon: Icon, label, tone }) => (
              <div className={`warning ${tone}`} key={label}>
                <Icon />
                <span>{label}</span>
              </div>
            ))}
        </div>

        <section className="public-contents">
          <header>
            <PackageOpen />
            <div>
              <h2>Contents</h2>
              <p>{unit.items.length} item entries in this inventory unit</p>
            </div>
          </header>
          {unit.items.length ? (
            <div className="public-item-list">
              {unit.items.map((item) => (
                <PublicItem key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <p className="public-empty">No contents have been recorded yet.</p>
          )}
        </section>

        {otherPhotos.length > 0 && (
          <section className="public-photo-section">
            <h2>Additional photos</h2>
            <div className="public-photo-grid">
              {otherPhotos.map((photo) => (
                <figure key={photo.id}>
                  <img src={photo.thumbnail_url || photo.url} alt={photo.caption || photo.category} />
                  <figcaption>{photo.caption || photo.category}</figcaption>
                </figure>
              ))}
            </div>
          </section>
        )}

        <section className="public-details">
          <h2>Tote information</h2>
          <dl>
            <Detail label="Description" value={unit.description} wide />
            <Detail label="Type" value={unit.inventory_type} />
            <Detail label="Category" value={unit.category} />
            <Detail label="Container" value={unit.container_type} />
            <Detail label="Size" value={unit.container_size} />
            <Detail label="Bin / item color" value={unit.container_color} />
            <Detail label="Lid color" value={unit.lid_color} />
            <Detail label="Owner" value={unit.owner} />
            <Detail label="Responsible person" value={unit.responsible_family_member} />
            <Detail label="Destination property" value={unit.destination_property} />
            <Detail label="Storage zone" value={unit.storage_zone} />
            <Detail label="U-Haul zone" value={unit.uhaul_loading_zone} />
            <Detail
              label="Approximate weight"
              value={unit.approximate_weight ? `${unit.approximate_weight} lb` : null}
            />
            <Detail
              label="Dimensions"
              value={
                unit.length || unit.width || unit.height
                  ? `${unit.length || "—"} × ${unit.width || "—"} × ${unit.height || "—"} in`
                  : null
              }
            />
            <Detail label="Estimated value" value={unit.estimated_total_value} />
            <Detail label="Priority" value={String(unit.priority)} />
            <Detail label="Notes" value={unit.general_notes} wide />
          </dl>
        </section>

        <div className="record-times public-record-times">
          <span>Last updated</span>
          <strong>{formatTime(unit.updated_at)}</strong>
          <span>Last scanned</span>
          <strong>{unit.last_scanned_at ? formatTime(unit.last_scanned_at) : "Never"}</strong>
        </div>
      </main>
    </div>
  );
}

function PublicItem({ item }: { item: InventoryItem }) {
  const details = [
    item.description,
    item.category,
    item.manufacturer,
    item.model && `Model ${item.model}`,
    item.serial_number && `Serial ${item.serial_number}`,
    item.asset_number && `Asset ${item.asset_number}`,
    item.condition,
    item.estimated_value && `$${item.estimated_value}`,
    item.fragile && "Fragile",
    item.essential_on_arrival && "Essential on arrival",
    item.notes
  ].filter(Boolean);

  return (
    <article>
      <div>
        <strong>{item.item_name}</strong>
        <span>
          {item.quantity} {item.unit_of_measure}
        </span>
      </div>
      {details.length > 0 && <p>{details.join(" · ")}</p>}
    </article>
  );
}

function Detail({
  label,
  value,
  wide = false
}: {
  label: string;
  value: string | null;
  wide?: boolean;
}) {
  if (!value) return null;
  return (
    <div className={wide ? "wide" : ""}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function formatTime(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));
}
